import type { IPackagingData, IPackagingComponent, IClaimsDisclaimerStatementsData, ICountryOfOriginStatementsData, IPrinterDielineDetailsData, IArtworkApproverDetailsData, IKosherSymbolData, IRegionSpecificIconsData, ISafetyProcessingEnvironmentalData, IRecyclingIconsData, IStorageHandlingDistributionData, IDocumentsData, IProductLabellingDetailsData, IBottleRedemptionInformationData, INonCopyInformationData, IIconsAndClaimsData } from "../types/OtherLabeling.types";
import { normalizeSectionNameOrDefault } from "@/utils/sectionNaming";
import {
  toIntegerPercentApiValue,
  toGramWeightApiString,
  toGramWeightFormValue,
  cookingInstructionsHtmlToPlainText,
} from "./fieldInputUtils";
import { getProductSpecificationFlags } from "@/utils/productSpecificationTypeUtils";
import { getPackagingSectionsVisibility } from "@/static/config/packagingVisibility";
import { formatActiveProjectMultiSelectIdsForSave } from "@/components/ActiveProjects/utils/helper";

function toString(value: any): string { return value == null ? '' : String(value); }


function isBackendId(value: any): boolean {
  if (value == null) return false;
  const str = String(value).trim();
  // Accept numeric ids or UUID-like ids
  if (/^\d+$/.test(str)) return true;
  if (/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(str)) return true;
  return false;
}

function toNumberId(value: any): number | string {
  if (value == null) return '';
  const str = String(value).trim();
  // Convert numeric ids to numbers, keep UUIDs as strings
  if (/^\d+$/.test(str)) return Number(str);
  return str;
}

function splitQuantityAndUom(qtyString: string): { qty: string; uom: string } {
  if (!qtyString) return { qty: '', uom: '' };
  const parts = qtyString.trim().split(/\s+/);
  if (parts.length === 1) return { qty: parts[0] ?? '', uom: '' };
  const uom = parts.pop() || '';
  return { qty: parts.join(' '), uom };
}

function mapPackagingComponents(components: IPackagingComponent[], levelType:  [{label: String, value: String}]) {
  return (components || []).map(c => {
    const level = c.level;
    const levelValue = levelType?.find((item: {label: String, value: String}) => 
      item?.value === level
    )
    const label = levelValue?.label?.toLowerCase() ?? '';

    const isPrimary = label.includes('primary')
    const isSecondary = label.includes('secondary')
    const isTertiary = label.includes('tertiary')
    
    const mapped: any = {
      ...(isBackendId((c as any).id) ? { id: toString((c as any).id) } : {}),
      packagingDescription: toString(c.packagingDescription),
      packagingComponentName: toString(c.packagingComponentName),
      // totalNumberOfComponents: c.totalNumberOfComponents ?? null,
      level: level,
      foodContact: toString(c.foodContact),
      specificationDefiningCharacteristics: toString((c as any).specificationDefiningCharacteristics),
      // weightOfComponent: Number((c as any).weightOfComponent ?? 0),
      percentageRecycledContent: toIntegerPercentApiValue(
        (c as any).percentageRecycledContent
      ),
      containBpa: toString((c as any).containBpa),
      weightOfRecycled: toGramWeightFormValue((c as any).weightOfRecycled),
      packagingSupplier: toString(c.packagingSupplier),
      countryOfOrigin: toString(c.countryOfOrigin),
      dimensions: toString(c.dimensions),
      material: toString(c.material),
      materialSubclass: toString((c as any).materialSubclass),
      iSCrvRequired: toString((c as any).iSCrvRequired),
      isPackagingTamperEvident: toString(c.isPackagingTamperEvident),
     
    };

    // EPR packaging component fields - umcomment when api integration
    mapped.isComponentComposedOfRecycledContent =  toString((c as any).isComponentComposedOfRecycledContent);
    mapped.isComponentCompostable = toString((c as any).isComponentCompostable);
    mapped.componentMeetAnyCompostabilityStandards = Array.isArray((c as any).componentMeetAnyCompostabilityStandards) 
      ? (c as any).componentMeetAnyCompostabilityStandards.map((val: any) => toString(val)).join(',')
      : '';
 
    if (isPrimary) {
      mapped.componentSingleUseFoodService = toString((c as any).componentSingleUseFoodService);
    }
    if (isPrimary) {
      mapped.componentSmallItem = toString((c as any).componentSmallItem);
    }
    mapped.isComponentMadeOfPlastic = toString((c as any).isComponentMadeOfPlastic);
    mapped.hasNonSeparableDifferentMaterial = toString((c as any).hasNonSeparableDifferentMaterial);
    
    // nonSeparablePartContainPlastic only included when hasNonSeparableDifferentMaterial is "yes"
    const nonSeparablePartValue = String((c as any).hasNonSeparableDifferentMaterial || '').toLowerCase().trim();
    if (nonSeparablePartValue === 'yes') {
      mapped.nonSeparablePartContainPlastic = toString((c as any).nonSeparablePartContainPlastic);
    }  
      // weightOfPlasticNonSeparablePartGrams only included when nonSeparablePartContainPlastic is "yes"
      const nonSeparablePartContainsPlasticValue = String((c as any).nonSeparablePartContainPlastic || '').toLowerCase().trim();
      if (nonSeparablePartContainsPlasticValue === 'yes') {
        mapped.weightOfPlasticNonSeparablePartGrams = toGramWeightApiString(
          (c as any).weightOfPlasticNonSeparablePartGrams
        );
      }
    // EPR packaging component fields
    
    return mapped;
  });
}

function mapRecycling(recycling: IRecyclingIconsData[]) {
  return (recycling || []).map(r => ({
    ...(isBackendId((r as any).id) ? { id: toString((r as any).id) } : {}),
    text: toString(r.text),
    supportingText: toString(r.supportingText),
    useOnPack: toString(r.useOnPack || ''),
  }));
}

function mapProductLabelingDetails(d: IProductLabellingDetailsData) {
  if (!d) return {};
  return {
    productDescriptor: toString(d.statementOfIdentity),
    productStory: toString(d.productStory),
    notesToDesign: toString(d.notesToDesign),
  };
}

const toJsonArrayString = (value: any): string | null => {
  if (value == null || value === '' || value === '[]') return null;
  if (typeof value === 'object') {
    return JSON.stringify([value]);
  }
  const raw = String(value);
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return JSON.stringify(parsed);
    if (parsed && typeof parsed === 'object') return JSON.stringify([parsed]);
  } catch { /* not json */ }
  return JSON.stringify([raw]);
};

function mapSafetyProcessingEnv(arr: ISafetyProcessingEnvironmentalData[]) {
  return (arr || []).map(s => {
    // Handle format: if statements array exists, convert to format string; otherwise use format field
    let formatValue = '';
    if (Array.isArray((s as any).statements) && (s as any).statements.length > 0) {
      // Convert statements array to comma-separated format string
      formatValue = (s as any).statements.map((o: any) => o?.option?.label || o?.option?.value || '').filter(Boolean).join(',');
    } else {
      formatValue = toString(s.format || '');
    }
    
    // Handle text: convert statements array to JSON string
    let textValue = null;
    if (Array.isArray((s as any).statements)) {
      try {
        textValue = (s as any)?.statements?.length > 0 ? JSON.stringify((s as any).statements) : null;
      } catch {
        textValue = null;
      }
    } else {
      textValue =  (s?.text == null || s?.text  === '' || s?.text  === '[]') ? null : s?.text;
    }
    
    return {
      ...(isBackendId((s as any).id) ? { id: toString((s as any).id) } : {}),
      format: formatValue,
      text: textValue,
      useOnPack: toString(s.useOnPack || ''),
    };
  });
}

function mapStorageHandling(arr: IStorageHandlingDistributionData[]) {
  return (arr || []).map(s => {
    // Handle format: if formats array exists, convert to format string; otherwise use format field
    let formatValue = '';
    if (Array.isArray((s as any).formats) && (s as any).formats.length > 0) {
      // Convert formats array to comma-separated format string
      formatValue = (s as any).formats.map((o: any) => o?.option?.label || o?.option?.value || '').filter(Boolean).join(',');
    } else {
      formatValue = toString(s.format || '');
    }
    
    // Handle text: convert formats array to JSON string
    let textValue = null;
    if (Array.isArray((s as any).formats)) {
      try {
        textValue = (s as any)?.formats?.length > 0 ? JSON.stringify((s as any).formats) : null;
      } catch {
        textValue = null;
      }
    } else {
      textValue =  (s?.text == null || s?.text  === '' || s?.text  === '[]') ? null : s?.text
    }
    
    return {
      ...(isBackendId((s as any).id) ? { id: toString((s as any).id) } : {}),
      format: formatValue,
      text: textValue,
    };
  });
}

function mapCodingStatements(arr: any[]) {
  return (arr || []).map((entry: any) => ({
    ...(isBackendId(entry?.id) ? { id: toString(entry.id) } : {}),
    statement: toString(entry?.statement),
    useOnPack: toString(entry?.useOnPack || ''),
  }));
}

function mapPrinterDetails(arr: IPrinterDielineDetailsData[]) {
  return (arr || []).map((p: any) => {
    return {
      ...(isBackendId((p as any).id) ? { id: toString((p as any).id) } : {}),
      companyName: toString(p.printerCompanyName ?? p.companyName),
      printerContactName: toString(p.printerContactName),
      emailAddress: toString(p.printerEmailAddress ?? p.emailAddress),
      phone: toString(p.printerPhone ?? p.phone),
      country: toString(p.printerCountry ?? p.country),
      addressLineOne: toString(p.printerAddressLine1 ?? p.addressLineOne),
      addressLineTwo: toString(p.printerAddressLine2 ?? p.addressLineTwo),
      city: toString(p.printerCity ?? p.city),
      state: toString(p.printerStateRegionProvince ?? p.state),
      zip: toString(p.printerZipPostalCode ?? p.zip),
      packagingComponent: toString(p.packagingComponent),
      printSubstrate: toString(p.printSubstrate),
      printProcess: toString(p.printProcess),
      color: toString(p.colors ?? p.maxNumberOfColors ?? p.color),
    };
  });
}

function mapOrganicClaim(arr: any[]) {
  return (arr || []).map(o => ({
    ...(isBackendId((o as any).id) ? { id: toString((o as any).id) } : {}),
    supportingTextForDeclaration: toString(o.supportingTextForDeclaration),
    icon: toString(o.icon),
    useOnPack: toString(o.useOnPack || ''),
  }));
}

function mapOrganicCertifier(arr: any[]) {
  return (arr || []).map(o => ({
    ...(isBackendId((o as any).id) ? { id: toString((o as any).id) } : {}),
    supportingTextForDeclaration: toString(o.supportingTextForDeclaration),
    icon: toString(o.icon),
    useOnPack: toString(o.useOnPack || ''),
  }));
}

function mapBottleRedemption(arr: IBottleRedemptionInformationData[]) {
  return (arr || []).map(b => ({
    ...(isBackendId((b as any).id) ? { id: toString((b as any).id) } : {}),
    bottleRedemptionStates: formatActiveProjectMultiSelectIdsForSave(b.bottleRedemptionStates), 
    supportingTextForDeclaration: toString(b.supportingTextForDeclaration),
    useOnPack: toString(b.useOnPack || ''),
  }));
}

function mapKosher(arr: IKosherSymbolData[]) {
  return (arr || []).map(k => ({
    ...(isBackendId((k as any).id) ? { id: toString((k as any).id) } : {}),
    onPackIcon: toString(k.onPackIcon || ''),
    useOnPack: toString(k.useOnPack || ''),
    supportingTextDeclaration: toString(k.supportingTextForDeclaration),
  }));
}

function mapKosherDesignation(arr: any[]) {
  return (arr || []).map(k => ({
    ...(isBackendId((k as any).id) ? { id: toString((k as any).id) } : {}),
    onPackIcon: toString(k.onPackIcon || ''),
    useOnPack: toString(k.useOnPack || ''),
    supportingTextDeclaration: toString(
      (k as any).supportingTextForDeclaration ??
        (k as any).supportingTextDeclaration ??
        ''
    ),
  }));
}

function mapProductSpecific(arr: IRegionSpecificIconsData[]) {
  return (arr || []).map(r => {
    const siteId = (r.site && typeof r.site === 'object')
      ? (r.site as { id?: string | number })?.id
      : r.site;
    return {
    ...(isBackendId((r as any).id) ? { id: toString((r as any).id) } : {}),
    site: siteId ? { id: toString(siteId) } : null,
    onPackIcons: toString(r.onPackIcons),
    useOnPack: toString(r.useOnPack || ''),
    supportingTextForDeclaration: toString(r.supportingTextForDeclaration),
    };
  });
}

function mapClaimsDisclaimerStatements(arr: IClaimsDisclaimerStatementsData[], isHaba: boolean = false, isFoodAndBeverages: boolean = false) {
  return (arr || []).map(c => {
    // Use the same logic for all fields (format, text, useOnPack) - same for both HABA and non-HABA
    const result: any = {
      ...(isBackendId((c as any).id) ? { id: toString((c as any).id) } : {}),
      useOnPack: toString(c?.useOnPack || ''),
    };
    
    // Handle format: if statements array exists, convert to format string; otherwise use format field
    if (Array.isArray((c as any).statements) && (c as any).statements.length > 0) {
      result.format = (c as any).statements.map((o: any) => o?.option?.label || o?.option?.value || '').filter(Boolean).join(',');
    } else if (Array.isArray((c as any).format)) {
      result.format = toCsv((c as any).format);
    } else {
      result.format = c?.format || '';
    }
    
    // Handle text: convert statements array to JSON string if it exists
    if (Array.isArray((c as any).statements)) {
      try {
        result.text = (c as any)?.statements?.length > 0 ? JSON.stringify((c as any).statements) : null;
      } catch {
        result.text = null;
      }
    } else {
      result.text =  (c?.text == null || c?.text  === '' || c?.text  === '[]') ? null : c?.text;
    }
    
    // For HABA: only add the two additional fields (cosmeticWarningPhrase and candleWarningPhrase)
    if (isHaba) {
      result.cosmeticWarningPhrase = toString((c as any).cosmeticWarningPhrase || '');
      result.candleWarningPhrase = toString((c as any).candleWarningPhrase || '');
    }
    
    // Food & Beverages: suitableForVegetarians + suitableForVegans; HABA: suitableForVegans only (below)
    if(isFoodAndBeverages) {
      result.suitableForVegetarians = toString((c as any).suitableForVegetarians || '');
      result.suitableForVegans = toString((c as any).suitableForVegans || '');
    }
    if(isHaba) {
      result.suitableForVegans = toString((c as any).suitableForVegans || '');
    }
    
    return result;
  });
}

function mapIconsAndClaims(arr: IIconsAndClaimsData[]) {
  return (arr || []).map(i => ({
    ...(isBackendId((i as any).id) ? { id: toString((i as any).id) } : {}),
    productClaims: toString(i.productClaims),
    cautionStatements: toString(i.cautionStatements),
    directionsForUse: toString(i.directionsForUse),
  }));
}

function toCsv(value: any): string {
  return formatActiveProjectMultiSelectIdsForSave(value, "|");
}

function mapCountryOfOrigin(arr: ICountryOfOriginStatementsData[] | any[], includeSupportingText = false) {
  return (arr || []).map((c: any) => {
    const out: any = {};
    if (isBackendId(c?.id)) out.id = toString(c.id);
    // Support both endpoint-shaped and form-shaped entries
    const stmt1Raw = (c?.countryOfOriginStatement1 !== undefined && c?.countryOfOriginStatement1 !== null)
      ? c?.countryOfOriginStatement1
      : '';
    const stmt2Raw = (c?.countryOfOriginStatement2 !== undefined && c?.countryOfOriginStatement2 !== null)
      ? c?.countryOfOriginStatement2
      : '';
    const country1 = c?.countryOfOriginCountry1;
    const country2 = c?.countryOfOriginCountry2;
    const reason = c?.reasonforCountryofOriginLabeling ?? c?.reasonForCountryOfOrigin;
    // Send statements as stringified JSON arrays per backend requirement
   
    out.countryOfOriginStatement1 = toJsonArrayString(stmt1Raw);
    out.countryOfOriginCountry1 = formatActiveProjectMultiSelectIdsForSave(country1);
    out.countryOfOriginStatement2 = toJsonArrayString(stmt2Raw);
    out.countryOfOriginCountry2 = formatActiveProjectMultiSelectIdsForSave(country2);
    out.reasonforCountryofOriginLabeling = toString(reason);
    out.cooRequiredOnLabel = toString(c?.cooRequiredOnLabel);
    if (includeSupportingText) {
      out.supportingText = toString(c?.supportingText);
    }
    return out;
  });
}

function mapDocuments(d: IDocumentsData, includeTraceabilityLabel = false) {
  const toDoc = (id: string, title: string) => ({ documentTitle: toString(title), document: { id: toNumberId(id) } });
  const toDielineDoc = (id: string | number, title: string) => ({ documentTitle: toString(title), document: { id: toNumberId(id) } });
  const docs: Record<string, unknown> = {
    attachments: (d?.packagingDocuments || []).map(file => isBackendId(file.id) ? toDoc(file.id, file.fileName) : { documentTitle: toString(file.fileName) }),
    dielineDocuments: (d?.dielineDocuments || []).map(file => isBackendId(file.id) ? toDielineDoc(file.id, file.fileName) : { documentTitle: toString(file.fileName) }),
  };
  if (includeTraceabilityLabel) {
    docs.traceabilityLabel = (d?.traceabilityLabelDocuments || []).map(file =>
      isBackendId(file.id) ? toDoc(file.id, file.fileName) : { documentTitle: toString(file.fileName) }
    );
  }
  return docs;
}

export function mapPackagingFormToApi(input: IPackagingData, productSpecificationType?: string, options?: any) {
  const isVendor = options?.isVendor === true;
  const { isFoodAndBeverages, isHaba, isPetFoods, isProduceRacTradersJoeLabel, isProduceRacBranded } = getProductSpecificationFlags(productSpecificationType);
  const sections = getPackagingSectionsVisibility(productSpecificationType);
  const showCooSupportingText = isFoodAndBeverages || isHaba || isPetFoods || isProduceRacTradersJoeLabel;
  const qtyParsed = splitQuantityAndUom(input?.weightUnitsSpecificationData?.quantities || '');

  // If form already uses endpoint-aligned fields, pass through with minimal shaping
  const passThrough: any = {};

  // Top-level weight fields
  if ((input as any).weightVolumePackQty || (input as any).weightVolumePackQtyUom) {
    passThrough.weightVolumePackQty = (input as any).weightVolumePackQty ?? '';
    passThrough.weightVolumePackQtyUom = (input as any).weightVolumePackQtyUom ?? '';
  } else {
    passThrough.weightVolumePackQty = qtyParsed.qty;
    passThrough.weightVolumePackQtyUom = qtyParsed.uom;
  }
  passThrough.declaredQuantityWeightVolumeType = (input as any).declaredQuantityWeightVolumeType ?? toString(input?.weightUnitsSpecificationData?.declaredQuantityType);
  passThrough.declaredQuantityPrintingBy = (input as any).declaredQuantityPrintingBy ?? toString(input?.weightUnitsSpecificationData?.declaredQuantityPrintingBy);
  passThrough.onPackDeclaredNetQuantityWeightVolume = (input as any).onPackDeclaredNetQuantityWeightVolume ?? toString(input?.weightUnitsSpecificationData?.netDeclaredWeightVolume);
  passThrough.locationOnPack = (input as any).locationOnPack ?? toString(input?.weightUnitsSpecificationData?.locationOnPack);
  passThrough.drainedWeight = (input as any).drainedWeight ?? toString(input?.weightUnitsSpecificationData?.drainedWeight);

  // Packaging components and quantity separable fields omitted while those sections are hidden from Other Labeling tab.

  passThrough.recyclingIcons = mapRecycling((input as any).recyclingIcons || input?.recyclingIconsData || [] as any);
  // Product labeling: TJ-only for vendor; optional in payload when vendor (only include when at least one value present)
  const productLabellingData = input?.productLabellingDetailsData;
  const pldStatement = toString(productLabellingData?.statementOfIdentity);
  const pldStory = toString(productLabellingData?.productStory);
  const pldNotes = toString(productLabellingData?.notesToDesign);
  const pldAllEmpty = pldStatement === '' && pldStory === '' && pldNotes === '';
  const shouldSetProductLabelingDetails = !isVendor || !pldAllEmpty;
  if (shouldSetProductLabelingDetails && (productLabellingData || ((input as any).productLabelingDetails && Array.isArray((input as any).productLabelingDetails) && (input as any).productLabelingDetails.length))) {
    if ((input as any).productLabelingDetails && Array.isArray((input as any).productLabelingDetails) && (input as any).productLabelingDetails.length) {
      const list = (input as any).productLabelingDetails as any[];
      const last = list[list.length - 1];
      const current = productLabellingData || {};
      const match = list.find((it: any) => toString(it?.productDescriptor) === toString(current?.statementOfIdentity));
      const selected = match || last || {};
      const built: any = {
        ...(isBackendId(selected?.id)
          ? { id: toString(selected.id) }
          : (Array.isArray((input as any).__original?.productLabelingDetailsIds) && (input as any).__original?.productLabelingDetailsIds?.length
              ? { id: (input as any).__original?.productLabelingDetailsIds[(input as any).__original?.productLabelingDetailsIds.length - 1] }
              : {})),
        productDescriptor: toString(current?.statementOfIdentity ?? selected?.productDescriptor),
        productStory: toString(current?.productStory ?? selected?.productStory),
        notesToDesign: toString(current?.notesToDesign ?? selected?.notesToDesign),
      };
      if (isVendor) {
        if (built.productDescriptor === '' || built.productDescriptor == null) delete built.productDescriptor;
        if (built.productStory === '' || built.productStory == null) delete built.productStory;
        if (built.notesToDesign === '' || built.notesToDesign == null) delete built.notesToDesign;
      }
      passThrough.productLabelingDetails = built;
    } else if (productLabellingData) {
      const mappedObj = mapProductLabelingDetails(productLabellingData as IProductLabellingDetailsData) as any;
      const fallbackId = Array.isArray((input as any)?.__original?.productLabelingDetailsIds) && (input as any).__original?.productLabelingDetailsIds.length
        ? (input as any).__original?.productLabelingDetailsIds[(input as any).__original?.productLabelingDetailsIds.length - 1]
        : undefined;
      const built: any = {
        ...(isBackendId(fallbackId) ? { id: fallbackId } : {}),
        ...mappedObj,
      };
      if (isVendor) {
        if (built.productDescriptor === '' || built.productDescriptor == null) delete built.productDescriptor;
        if (built.productStory === '' || built.productStory == null) delete built.productStory;
        if (built.notesToDesign === '' || built.notesToDesign == null) delete built.notesToDesign;
      }
      passThrough.productLabelingDetails = built;
    }
  }
  passThrough.safetyProcessingEnvStatements = mapSafetyProcessingEnv((input as any).safetyProcessingEnvStatements || [] as any);
  passThrough.storageHandlingDistributionStatements = mapStorageHandling((input as any).storageHandlingDistributionStatements || input?.storageHandlingDistributionData || [] as any);
  passThrough.codingStatements = mapCodingStatements((input as any).codingStatements || input?.codingStatementsData || [] as any);
  const printerDielineArr = (input as any).printerDielineDetailsData || (input as any).printerDetails || [];
  passThrough.printerDetails = mapPrinterDetails(Array.isArray(printerDielineArr) ? printerDielineArr : []);
  passThrough.organicClaim = mapOrganicClaim((input as any).organicClaim || input?.organicClaim || [] as any);
  passThrough.organicCertifier = mapOrganicCertifier((input as any).organicCertifier || input?.organicCertifier || [] as any);
  if (sections.bottleRedemptionInformation) {
    passThrough.bottleRedemptionInformation = mapBottleRedemption(
      (input as any).bottleRedemptionInformation || input?.bottleRedemptionInformation || [] as any
    );
  }

  // Artwork Approver Details: send as top-level fields (single entry)
  const artworkApproverName = (input as any).artworkApproverName ?? toString(input?.artworkApproverDetailsData?.artworkApproverName);
  const artworkApproverEmail = (input as any).artworkApproverEmail ?? toString(input?.artworkApproverDetailsData?.artworkApproverEmail);
  const artworkApproverPhone = (input as any).artworkApproverPhoneNumber ?? toString(input?.artworkApproverDetailsData?.artworkApproverPhone);
  passThrough.artworkApproverName = artworkApproverName;
  passThrough.artworkApproverEmail = artworkApproverEmail;
  passThrough.artworkApproverPhoneNumber = artworkApproverPhone;

  // Non-copy: TJ-only fields; for vendor they are optional
  const designCommentsVal = (input as any).designComments ?? toString(input?.nonCopyInformationData?.designComments);
  const reasonForIssueVal = (input as any).reasonForIssue ?? toString(input?.nonCopyInformationData?.reasonForIssue);
  const designTeamVal = (input as any).designTeam ?? toString(input?.nonCopyInformationData?.designTeam);
  const anyOtherInfoBackOfPackVal = (input as any).anyOtherInfoBackOfPack ?? toString(input?.nonCopyInformationData?.anyOtherInfoBackOfPack);
  const photographyDateProductsReadyToShootVal = (input as any).photography ?? toString(input?.nonCopyInformationData?.photographyDateProductsReadyToShoot);
  if (isVendor) {
    if (designCommentsVal != null && String(designCommentsVal).trim() !== '') passThrough.designComments = designCommentsVal;
    if (reasonForIssueVal != null && String(reasonForIssueVal).trim() !== '') passThrough.reasonForIssue = reasonForIssueVal;
    if (designTeamVal != null && String(designTeamVal).trim() !== '') passThrough.designTeam = designTeamVal;
    if (anyOtherInfoBackOfPackVal != null && String(anyOtherInfoBackOfPackVal).trim() !== '') passThrough.anyOtherInfoBackOfPack = anyOtherInfoBackOfPackVal;
    if (photographyDateProductsReadyToShootVal != null && String(photographyDateProductsReadyToShootVal).trim() !== '') passThrough.photography = photographyDateProductsReadyToShootVal;
  } else {
    passThrough.designComments = designCommentsVal;
    passThrough.reasonForIssue = reasonForIssueVal;
    passThrough.designTeam = designTeamVal;
    passThrough.anyOtherInfoBackOfPack = anyOtherInfoBackOfPackVal;
    passThrough.photography = photographyDateProductsReadyToShootVal;
  }
  // For vendor: ensure empty TJ-only fields are never sent (omit keys entirely; do not send designTeam="" etc.)
  if (isVendor) {
    if (passThrough.designComments === '' || passThrough.designComments == null) delete passThrough.designComments;
    if (passThrough.reasonForIssue === '' || passThrough.reasonForIssue == null) delete passThrough.reasonForIssue;
    if (passThrough.designTeam === '' || passThrough.designTeam == null) delete passThrough.designTeam;
    if (passThrough.anyOtherInfoBackOfPack === '' || passThrough.anyOtherInfoBackOfPack == null) delete passThrough.anyOtherInfoBackOfPack;
    if (passThrough.photography === '' || passThrough.photography == null) delete passThrough.photography;
  }

  // Cooking instructions: save 'comments' at top-level (no cookingInstructions wrapper)
  if (sections.cookingInstructions) {
    if ((input as any).cookingInstructions) {
      const ci = (input as any).cookingInstructions as any;
      if (ci && typeof ci === 'object' && 'comments' in ci) {
        passThrough.comments = cookingInstructionsHtmlToPlainText((ci as any).comments);
      }
    } else if ((input as any).cookingInstructionsData) {
      passThrough.comments = cookingInstructionsHtmlToPlainText(
        (input as any).cookingInstructionsData?.comments
      );
    }
  }

  // Country of Origin / Kosher / Specific Icons / Claims
  passThrough.countryOfOriginStatements = mapCountryOfOrigin(
    (input as any).countryOfOriginStatements || input?.countryOfOriginStatementsData || [] as any,
    showCooSupportingText
  );
  passThrough.kosherSymbols = mapKosher((input as any).kosherSymbol || input?.kosherSymbolData || [] as any);
  passThrough.kosherDesignations = mapKosherDesignation((input as any).kosherDesignation || input?.kosherDesignation || [] as any);
 
  passThrough.claimsDisclaimerStatements = mapClaimsDisclaimerStatements((input as any).claimsDisclaimerStatements || input?.claimsDisclaimerStatementsData || [] as any);
  
  if(isHaba || isPetFoods) {
    passThrough.iconsAndClaims = mapIconsAndClaims((input as any).iconsAndClaims || input?.iconsAndClaimsData || [] as any);
  }

  if(isFoodAndBeverages) {
    passThrough.productSpecificIconsStamps = mapProductSpecific((input as any).productSpecificIconsStamps || input?.regionSpecificIconsData || [] as any);
  }
  
  // Always use claimsDisclaimerStatementsData and map to claimsDisclaimerStatements in payload
  // For HABA, include cosmeticWarningPhrase and candleWarningPhrase fields
  // Always read from claimsDisclaimerStatementsData (not the endpoint-aligned claimsDisclaimerStatements) to ensure all fields are included
  const claimsData = input?.claimsDisclaimerStatementsData || (input as any).claimsDisclaimerStatements || [];
  passThrough.claimsDisclaimerStatements = mapClaimsDisclaimerStatements(claimsData, isHaba, isFoodAndBeverages);

  // Documents
  if ((input as any).documents || (input as any).attachments || (input as any).dielineDocuments || (input as any).traceabilityLabel) {
    // if ((input as any).documents) passThrough.documents = (input as any).documents;
    if ((input as any).attachments) {
      // Sanitize: UI can emit placeholder ids like "doc-0" after delete; backend should not receive a document object then.
      passThrough.attachments = Array.isArray((input as any).attachments)
        ? (input as any).attachments.map((doc: any) => {
            if (doc && typeof doc === 'object' && doc.document) {
              const id = doc.document?.id;
              if (id != null && isBackendId(id)) {
                return {
                  ...doc,
                  document: { ...doc.document, id: toNumberId(id) }
                };
              }
              // Not a backend id (ex: "doc-0") → drop document object
              const { document, ...rest } = doc;
              return rest;
            }
            return doc;
          })
        : (input as any).attachments;
    }
    if ((input as any).dielineDocuments) {
      // Convert dieline document ids to numbers
      passThrough.dielineDocuments = Array.isArray((input as any).dielineDocuments)
        ? (input as any).dielineDocuments.map((doc: any) => {
            if (doc && typeof doc === 'object' && doc.document) {
              const id = doc.document?.id;
              if (id != null && isBackendId(id)) {
                return {
                  ...doc,
                  document: {
                    ...doc.document,
                    id: toNumberId(id)
                  }
                };
              }
              // Not a backend id (ex: "doc-0") → drop document object
              const { document, ...rest } = doc;
              return rest;
            }
            return doc;
          })
        : (input as any).dielineDocuments;
    }
    if (isProduceRacTradersJoeLabel && (input as any).traceabilityLabel) {
      passThrough.traceabilityLabel = Array.isArray((input as any).traceabilityLabel)
        ? (input as any).traceabilityLabel.map((doc: any) => {
            if (doc && typeof doc === 'object' && doc.document) {
              const id = doc.document?.id;
              if (id != null && isBackendId(id)) {
                return {
                  ...doc,
                  document: { ...doc.document, id: toNumberId(id) },
                };
              }
              const { document, ...rest } = doc;
              return rest;
            }
            return doc;
          })
        : (input as any).traceabilityLabel;
    }
  } else {
    const docs = mapDocuments(input?.documentsData as IDocumentsData, isProduceRacTradersJoeLabel);
    Object.assign(passThrough, docs);
  }

  // Deleted IDs tracking - compute by comparing endpoint arrays vs current mapped arrays
  const collectExistingIds = (arr: any[]) => new Set((arr || []).map((x: any) => (x && typeof x === 'object' ? x?.id : x)).filter(isBackendId));
  const diffIds = (existing: Set<any>, current: any[]) => {
    // Normalize IDs to strings for comparison to handle type mismatches (number vs string)
    const normalizeId = (id: any): string => String(id);
    const now = new Set((current || []).map((x: any) => x?.id).filter(isBackendId).map(normalizeId));
    const deleted = Array.from(existing).filter((id: any) => !now.has(normalizeId(id))).map(toNumberId);
    return deleted.length ? deleted : undefined;
  };

  // Compute diffs for arrays with deletable items
  const existingRecycling = collectExistingIds((input as any).__original?.recyclingIcons || (input as any).recyclingIcons || []);
  passThrough.deletedRecyclingIconIds = diffIds(existingRecycling, passThrough.recyclingIcons);

  const existingProductSpecific = collectExistingIds((input as any).__original?.productSpecificIconsStamps || (input as any).__original?.productSpecificIconsLegends || (input as any).productSpecificIconsStamps || (input as any).productSpecificIconsLegends || []);
  if (isFoodAndBeverages) {
    passThrough.deletedProductSpecificIconIds = diffIds(existingProductSpecific, passThrough.productSpecificIconsStamps);
  }

  const existingKosher = collectExistingIds((input as any).__original?.kosherSymbol || (input as any).kosherSymbol || []);
  passThrough.deletedKosherSymbolIds = diffIds(existingKosher, passThrough.kosherSymbols);
  
  const existingKosherDesignation = collectExistingIds((input as any).__original?.kosherDesignation || (input as any).kosherDesignation || []);
  passThrough.deletedKosherDesignationIds = diffIds(existingKosherDesignation, passThrough.kosherDesignations);

  const existingClaims = collectExistingIds((input as any).__original?.claimsDisclaimerStatements || (input as any).claimsDisclaimerStatements || []);
  passThrough.deletedClaimsIds = diffIds(existingClaims, passThrough.claimsDisclaimerStatements);

  const existingSafety = collectExistingIds((input as any).__original?.safetyProcessingEnvStatements || (input as any).safetyProcessingEnvStatements || []);
  passThrough.deletedSafetyProcesingEnvIds = diffIds(existingSafety, passThrough.safetyProcessingEnvStatements);

  const existingStorage = collectExistingIds((input as any).__original?.storageHandlingDistributionStatements || (input as any).storageHandlingDistributionStatements || []);
  passThrough.deletedStorageHandlingDistIds = diffIds(existingStorage, passThrough.storageHandlingDistributionStatements);

  const existingCodingStatements = collectExistingIds((input as any).__original?.codingStatements || (input as any).codingStatements || []);
  passThrough.codingStatementsToDelete = diffIds(existingCodingStatements, passThrough.codingStatements);

  const existingCountry = collectExistingIds((input as any).__original?.countryOfOriginStatements || (input as any).countryOfOriginStatements || []);
  passThrough.deletedCountryOfOriginStatementsIds = diffIds(existingCountry, passThrough.countryOfOriginStatements);

  const existingPrinter = collectExistingIds((input as any).__original?.printerDetails || (input as any).__original?.printerDielineDetails || (input as any).printerDetails || (input as any).printerDielineDetails || []);
  passThrough.deletedPrinterDetailsIds = diffIds(existingPrinter, passThrough.printerDetails);

  const existingOrganicClaim = collectExistingIds((input as any).__original?.organicClaim || (input as any).organicClaim || []);
  passThrough.deletedOrganicClaimIds = diffIds(existingOrganicClaim, passThrough.organicClaim);

  const existingOrganicCertifier = collectExistingIds((input as any).__original?.organicCertifier || (input as any).organicCertifier || []);
  passThrough.deletedOrganicCertifierIds = diffIds(existingOrganicCertifier, passThrough.organicCertifier);

  if (sections.bottleRedemptionInformation) {
    const existingBottleRedemption = collectExistingIds((input as any).__original?.bottleRedemptionInformation || (input as any).bottleRedemptionInformation || []);
    passThrough.deletedBottleRedemptionInformationIds = diffIds(existingBottleRedemption, passThrough.bottleRedemptionInformation);
  }

  if (isHaba || isPetFoods) {
    const existingIconsAndClaims = collectExistingIds((input as any).__original?.iconsAndClaims || (input as any).iconsAndClaims || []);
    passThrough.deletedIconsClaimsIds = diffIds(existingIconsAndClaims, passThrough.iconsAndClaims);
  }

  passThrough.isOtherLabellingValidated =
    (input as any).isPackagingValidated ??
    (input as any).isOtherLabellingValidated ??
    false;

  // Unified convention: if sectionName is empty, persist it as "Untitled".
  // Preserve legacy behavior of only sending the field when it's present on input.
  if ((input as any)?.sectionName !== undefined && (input as any)?.sectionName !== null) {
    passThrough.sectionName = normalizeSectionNameOrDefault((input as any).sectionName);
  }

  return passThrough;
}

/** Merges current form data with `isOtherLabellingValidated` JSON from `validateOverall` (Vendor/TJ flags). */
export function buildPackagingPayloadWithValidation(
  formData: IPackagingData | Record<string, unknown>,
  validationResult: { vendor: boolean; tj: boolean }
): Record<string, unknown> {
  return {
    ...(formData as Record<string, unknown>),
    isOtherLabellingValidated: JSON.stringify({
      Vendor: validationResult.vendor,
      TJ: validationResult.tj,
    }),
  };
}

export default mapPackagingFormToApi;


